import React, { useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  ChevronRight,
  Clock,
  Minus,
  Send,
  TrendingUp,
  Users,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { ScrollArea } from "../../../components/ui/scrollArea";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as UITooltip,
} from "../../../components/ui/tooltip";
import { Badge } from "../../../components/ui/badge";
import { Separator } from "../../../components/ui/seprator";
import { Button } from "../../../components/ui/button";
import { cn } from "../../../components/ui/utils";
import { useMediaQuery } from "@mui/material";
import moment from "moment";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
  Legend,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
} from "recharts";
import {
  format,
  parse,
  isValid,
  addHours,
  isBefore,
  differenceInDays,
  startOfDay,
  subDays,
  isSameDay,
  isAfter,
  addMinutes,
} from "date-fns";

const KPICard = ({ label, value, sublabel, delta, icon, status, onClick }) => {
  const borderColor =
    {
      success: "border-l-green-500",
      warning: "border-l-orange-500",
      danger: "border-l-red-500",
      info: "border-l-blue-500",
    }[status] ?? "border-l-gray-300";

  const iconColor =
    {
      success: "text-green-600",
      warning: "text-orange-600",
      danger: "text-red-600",
      info: "text-blue-600",
    }[status] ?? "text-gray-600";

  return (
    <Card
      className={cn(
        "border-l-4 shadow-sm cursor-pointer group transition-all duration-300 hover:shadow-xl hover:scale-[1.02] hover:-translate-y-0.5",
        "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]",
        borderColor,
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick?.();
      }}
      aria-label={`View ${label} details`}
    >
      <CardContent className="p-20 sm:p-24 min-h-[180px] flex flex-col">
        <div className="flex items-start justify-between mb-12">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-muted-foreground tracking-tight">
              {label}
            </p>
            {sublabel && (
              <p className="text-xs text-muted-foreground mt-1">{sublabel}</p>
            )}
          </div>
          <div className={cn("shrink-0 text-2xl", iconColor)}>{icon}</div>
        </div>

        <div className="flex-1 flex items-center">
          <p className="text-3xl sm:text-4xl font-bold text-foreground tracking-tight">
            {value}
          </p>
        </div>

        {delta !== undefined && (
          <div className="flex items-center gap-1.5 mb-4">
            {delta > 0 ? (
              <ArrowUpRight className="h-4 w-4 text-green-600 shrink-0" />
            ) : delta < 0 ? (
              <ArrowDownRight className="h-4 w-4 text-red-600 shrink-0" />
            ) : (
              <Minus className="h-4 w-4 text-gray-600 shrink-0" />
            )}
            <span
              className={cn(
                "text-xs font-medium",
                delta > 0
                  ? "text-green-600"
                  : delta < 0
                    ? "text-red-600"
                    : "text-gray-600",
              )}
            >
              {delta > 0 ? "+" : ""}
              {Math.abs(delta)} vs yesterday
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatusCard = ({ label, value, variant = "info" }) => {
  const variants = {
    success: {
      bg: "bg-green-100 dark:bg-green-900/30",
      text: "text-green-700 dark:text-green-400",
    },
    warning: {
      bg: "bg-orange-100 dark:bg-orange-900/30",
      text: "text-orange-700 dark:text-orange-400",
    },
    danger: {
      bg: "bg-red-100   dark:bg-red-900/30",
      text: "text-red-700   dark:text-red-400",
    },
    info: {
      bg: "bg-blue-100   dark:bg-blue-900/30",
      text: "text-blue-700   dark:text-blue-400",
    },
  };

  const { bg, text } = variants[variant] ?? variants.info;

  return (
    <Card className="shadow-sm hover:shadow-md transition-all dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
      <CardContent className="p-16 sm:p-20">
        <p className="text-xs sm:text-sm text-muted-foreground font-medium mb-8">
          {label}
        </p>
        <p className={cn("text-2xl sm:text-3xl font-bold", text)}>{value}</p>
      </CardContent>
    </Card>
  );
};

function calculateLiveKPIs(appointments, referenceDate = new Date()) {
  let totalRequiredShifts = 0;
  let onTimeCompliant = 0;
  let missingProofs = 0;
  let lateSubmissions = 0;
  let invalidProofs = 0;
  let inProgress = 0;
  let proofDueSoon = 0;
  let overdueProof = 0;

  for (const ap of appointments) {
    const startStr = ap.appointmentDateTime || ap.apoointmentDetails?.appointmentDateTime;
    if (!startStr) continue;
    
    const start = new Date(startStr);
    if (!isValid(start)) continue;

    const endTimeStr = ap.apoointmentDetails?.endTime;
    if (!endTimeStr) continue;

    let end;
    try {
      const datePart = format(start, "yyyy-MM-dd");
      end = parse(endTimeStr, "hh:mm a", new Date(`${datePart} ${endTimeStr}`));
      
      if (!isValid(end)) {
        end = addHours(start, 8);
      }
      
      if (isBefore(end, start)) {
        end = addHours(end, 24);
      }
    } catch {
      continue;
    }

    const deadline = end;

    if (referenceDate >= start && referenceDate < end) {
      inProgress++;
    }

    const allTasks = [];
    if (ap.tasks) {
      Object.values(ap.tasks).forEach((group) => {
        if (group?.tasksList?.length) {
          allTasks.push(...group.tasksList);
        }
      });
    }

    const requiredTasks = allTasks?.filter((t) => t.pos === true);

    if (requiredTasks.length === 0) {
      continue;
    }

    totalRequiredShifts++;

    const minsLeft = (end.getTime() - referenceDate.getTime()) / 60000;
    if (minsLeft > 0 && minsLeft <= 60) {
      proofDueSoon++;
    }

    let hasMissing = false;
    let hasInvalid = false;
    let hasLate = false;

    for (const task of requiredTasks) {
      const proofs = task.proofs || [];

      if (proofs.length === 0) {
        hasMissing = true;
        continue;
      }

      const validProofs = proofs.filter(p => p.verified === true && p.source === 'camera');

      if (validProofs.length === 0) {
        // No valid proofs = INVALID
        hasInvalid = true;
        continue;
      }

      const isTaskLate = proofs.some(p => {
        let capturedAt = p.captured_at_device;
        
        if (capturedAt && typeof capturedAt === 'object' && capturedAt.seconds) {
          capturedAt = new Date(capturedAt.seconds * 1000);
        } else if (capturedAt) {
          capturedAt = new Date(capturedAt);
        }
        
        return isValid(capturedAt) && isAfter(capturedAt, deadline);
      });

      if (isTaskLate) {
        hasLate = true;
      }
    }

    if (hasMissing) {
      if (referenceDate > deadline) {
        overdueProof++;
      }
      missingProofs++;
    } else if (hasInvalid) {
      invalidProofs++;
    } else if (hasLate) {
      lateSubmissions++;
    } else {
      onTimeCompliant++;
    }
  }

  const compliance = totalRequiredShifts > 0
    ? Math.round((onTimeCompliant / totalRequiredShifts) * 100)
    : 0;

  return {
    shiftCompliance: compliance,
    missingProofs,
    lateSubmissions,
    invalidProofs,
    inProgress,
    proofDueSoon,
    overdueProof,
    completed: onTimeCompliant,
    totalRequiredShifts,
  };
}

const OverviewHeader = ({ 
  period = "today", 
  rawAppointments = [], 
  clients = [],
  employees,
  showAdvancedView = false,
  setShowMessageModal,
  setMessageRecipients,
  setSelectedEmployee,
  setShowEmployeeDetailModal,
  setEmployeeHistoryTimeFrame
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const now = new Date();

  const filteredAppointments = useMemo(() => {
    const today = startOfDay(now);
    let startDate;

    switch (period) {
      case "today":
        startDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        break;
      case "last7":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 6);
        break;
      case "last30":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case "all":
      default:
        return rawAppointments;
    }

    return rawAppointments?.filter((ap) => {
      const d = new Date(
        ap.appointmentDateTime || ap.apoointmentDetails?.appointmentDateTime,
      );
      return isValid(d) && startOfDay(d) >= startDate && startOfDay(d) <= today;
    });
  }, [rawAppointments, period]);

  const kpi = useMemo(
    () => calculateLiveKPIs(filteredAppointments, now),
    [filteredAppointments, now]
  );

  const yesterdayApps = useMemo(() => {
    return rawAppointments?.filter((ap) => {
      const d = new Date(
        ap.appointmentDateTime || ap.apoointmentDetails?.appointmentDateTime,
      );
      return isValid(d) && differenceInDays(now, d) === 1;
    });
  }, [rawAppointments, now]);

  const yesterdayKpi = useMemo(() => {
    const yesterdayDate = new Date(now.getTime() - 86400000);
    return calculateLiveKPIs(yesterdayApps, yesterdayDate);
  }, [yesterdayApps, now]);

  const trend = useMemo(() => {
    const days = [];
    const referenceDate = new Date();
    
    const daysToShow = period === "last30" ? 30 : 7;

    for (let i = daysToShow - 1; i >= 0; i--) {
      const d = new Date(referenceDate);
      d.setDate(d.getDate() - i);
      days.push(d);
    }

    return days.map((day) => {
      const dateLabel = format(day, "MMM d");
      const priorDate = new Date(day);
      priorDate.setDate(priorDate.getDate() - 7);

      const getStats = (targetDate) => {
        const targetStr = format(targetDate, "yyyy-MM-dd");
        
        const shifts = filteredAppointments?.filter((shift) => {
          const sDate = shift.appointmentDateTime || shift.apoointmentDetails?.appointmentDateTime;
          if (!sDate) return false;
          return format(new Date(sDate), "yyyy-MM-dd") === targetStr;
        }) || [];

        let total = 0;
        let compliant = 0;

        shifts.forEach((shift) => {
          let hasPos = false;
          if (shift.tasks) {
            Object.values(shift.tasks).forEach(group => {
              group.tasksList?.forEach(task => {
                if (task.pos === true) {
                  hasPos = true;
                }
              });
            });
          }

          if (!hasPos) return;

          total++;

          try {
            const shiftDate = new Date(targetStr);
            let endTimeStr = shift.apoointmentDetails?.endTime;
            
            if (!endTimeStr) return;

            let parsedEnd = parse(endTimeStr, "h:mm a", shiftDate);
            if (!isValid(parsedEnd)) {
              parsedEnd = parse(endTimeStr, "hh:mm a", shiftDate);
            }
            if (!isValid(parsedEnd)) {
              return;
            }

            const startStr = shift.appointmentDateTime || shift.apoointmentDetails?.appointmentDateTime;
            const start = startStr ? new Date(startStr) : shiftDate;
            
            if (isValid(start) && isBefore(parsedEnd, start)) {
              parsedEnd = addHours(parsedEnd, 24);
            }

            const deadline = parsedEnd;

            let hasMissing = false;
            let hasInvalid = false;
            let hasLate = false;

            if (shift.tasks) {
              Object.values(shift.tasks).forEach(group => {
                group.tasksList?.forEach(task => {
                  if (task.pos === true) {
                    const taskProofs = task.proofs || [];
                    
                    // Check 1: No proofs = MISSING
                    if (taskProofs.length === 0) {
                      hasMissing = true;
                      return;
                    }

                    // Check 2: Valid proofs (verified + camera)
                    const validProofs = taskProofs.filter(
                      p => p.verified === true && p.source === 'camera'
                    );

                    if (validProofs.length === 0) {
                      // No valid proofs = INVALID
                      hasInvalid = true;
                      return;
                    }

                    // Check 3: Late proofs (ONLY time check on ANY proof)
                    const hasLateProof = taskProofs.some(p => {
                      let capturedAt = p.captured_at_device;
                      
                      if (capturedAt && typeof capturedAt === 'object' && capturedAt.seconds) {
                        capturedAt = new Date(capturedAt.seconds * 1000);
                      } else if (capturedAt) {
                        capturedAt = new Date(capturedAt);
                      }
                      
                      return isValid(capturedAt) && isAfter(capturedAt, deadline);
                    });

                    if (hasLateProof) {
                      hasLate = true;
                    }
                  }
                });
              });
            }

            // Priority: missing > invalid > late > compliant
            if (!hasMissing && !hasInvalid && !hasLate) {
              compliant++;
            }

          } catch (err) {
            console.warn("Error processing shift:", err);
          }
        });

        return total > 0 ? Math.round((compliant / total) * 100) : 0;
      };

      return {
        date: dateLabel,
        compliance: getStats(day),
        priorPeriod: getStats(priorDate),
      };
    });
  }, [filteredAppointments, period]);

  // 9. At-Risk Employees (Last 30 Days)
  const atRiskEmployees = useMemo(() => {
    if (!filteredAppointments?.length || !employees?.length) return [];

    const now = new Date();
    const userStats = {};

    const clientMap =
      clients?.reduce((acc, c) => {
        acc[c.id] = c.clientName || c.firstName;
        return acc;
      }, {}) || {};

    const employeeMap =
      employees?.reduce((acc, e) => {
        acc[e.id] = e;
        return acc;
      }, {}) || {};

    filteredAppointments?.forEach((appt) => {
      const apptDate = new Date(
        appt.appointmentDateTime ||
          appt.apoointmentDetails?.appointmentDateTime
      );
      if (!isValid(apptDate)) return;

      const uid = appt.userId;
      if (!uid || !employeeMap[uid]) return;

      if (!userStats[uid]) {
        const emp = employeeMap[uid];
        userStats[uid] = {
          id: emp.id,
          name: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
          email: emp.email,
          phone: emp.phoneNumber,
          photo: emp.photo,
          total: 0,
          onTime: 0,
          late: 0,
          missing: 0,
          invalid: 0,
          detailedShifts: [],
          lastShiftTime: 0,
          lastSiteId: null,
        };
      }

      const stats = userStats[uid];
      const details = appt.apoointmentDetails || {};

      let shiftEnd;
      try {
        if (details.endTime) {
          const datePart = format(apptDate, "yyyy-MM-dd");
          shiftEnd = parse(
            details.endTime,
            "hh:mm a",
            new Date(`${datePart} ${details.endTime}`)
          );

          if (!isValid(shiftEnd)) {
            shiftEnd = addMinutes(apptDate, details.duration || 180);
          }

          if (isBefore(shiftEnd, apptDate)) {
            shiftEnd = addHours(shiftEnd, 24);
          }
        } else {
          shiftEnd = addMinutes(apptDate, details.duration || 180);
        }
      } catch {
        shiftEnd = addMinutes(apptDate, details.duration || 180);
      }

      if (isBefore(now, shiftEnd)) return;

      let requiredCount = 0;
      let providedCount = 0;
      let lateCount = 0;

      let hasMissing = false;
      let hasInvalid = false;
      let hasLate = false;

      const collectedPhotos = [];

      Object.values(appt.tasks || {}).forEach((group) => {
        group.tasksList?.forEach((task) => {
          if (task.pos === true) {
            requiredCount++;

            const proofs = task.proofs || [];

            if (proofs.length === 0) {
              hasMissing = true;
              return;
            }

            providedCount++;

            proofs.forEach((p) => {
              if (p.url) {
                collectedPhotos.push({
                  url: p.url,
                  verified: p.verified === true,
                  gps: p.gps,
                  capturedAt: p.captured_at_device,
                });
              }

              if (p.verified !== true || p.source !== "camera") {
                hasInvalid = true;
              }

              const capturedAt = p.captured_at_device
                ? new Date(p.captured_at_device)
                : null;

              if (
                capturedAt &&
                isValid(capturedAt) &&
                isAfter(capturedAt, shiftEnd)
              ) {
                hasLate = true;
                lateCount++;
              }
            });
          }
        });
      });

      if (requiredCount === 0) return;

      let shiftStatus = "complete";
      let statusText = "Verified";
      let locationMessage = "Verified via geofencing";

      if (hasMissing) {
        shiftStatus = "missing";
        statusText = "Missing Proof";
        locationMessage = "Missing Proof";
        stats.missing++;
      } else if (hasInvalid) {
        shiftStatus = "missing";
        statusText = "Not Verified";
        locationMessage = "Not Verified";
        stats.invalid++;
      } else if (hasLate) {
        shiftStatus = "late";
        statusText = "Late Proof";
        locationMessage = "Late Proof";
        stats.late++;
      } else {
        stats.onTime++;
      }

      stats.total++;

      if (apptDate.getTime() > stats.lastShiftTime) {
        stats.lastShiftTime = apptDate.getTime();
        stats.lastSiteId = appt.clientId;
      }

      stats.detailedShifts.push({
        date: format(apptDate, "MMM dd, yyyy"),
        shiftName: details.shiftName || "Shift",
        site: clientMap[appt.clientId] || "Unknown",
        startTime: details.time || "-",
        endTime: details.endTime || "-",
        status: shiftStatus,
        duration: details.duration || "-",
        statusText,
        requiredProofTasks: requiredCount,
        providedProofTasks: providedCount,
        lateProofTasks: lateCount,
        photoCount: collectedPhotos.length,
        photos: collectedPhotos,
        location: locationMessage,
      });
    });

    return Object.values(userStats)
      ?.filter((u) => u.detailedShifts.length > 0)
      ?.map((u) => {
        const riskScore = u.missing + u.invalid + u.late * 0.5;
        const completionRate =
          u.total > 0 ? Math.round((u.onTime / u.total) * 100) : 0;

        return {
          id: u.id,
          name: u.name,
          email: u.email,
          phone: u.phone,
          photo: u.photo,
          completionRate,
          missingProofs: u.missing,
          invalidProofs: u.invalid,
          lateProofs: u.late,
          completed: u.onTime,
          totalShifts: u.total,
          lastShift: u.lastShiftTime
            ? format(new Date(u.lastShiftTime), "MMM dd, yyyy")
            : "-",
          site: clientMap[u.lastSiteId] || "Unknown",
          riskScore,
          status:
            u.missing > 0 ? "missing" : u.late > 0 ? "late" : "complete",
          detailedShifts: u.detailedShifts.sort(
            (a, b) => moment(b.date).valueOf() - moment(a.date).valueOf()
          ),
        };
      })
      ?.sort((a, b) => b.riskScore - a.riskScore)
      ?.slice(0, 5);
  }, [filteredAppointments, employees, clients]);

  // 11. Proof Breakdown (Stacked Area Chart)
  const proofBreakdownData = useMemo(() => {
      const days = [];
      const referenceDate = new Date();
      const daysToShow = period === "last30" ? 30 : 7;

      for (let i = daysToShow - 1; i >= 0; i--) {
          const d = new Date(referenceDate);
          d.setDate(d.getDate() - i);
          
          const dayStart = startOfDay(d);
          const dayEnd = new Date(dayStart);
          dayEnd.setHours(23, 59, 59, 999);

          // Filter appointments for this day
          const dayAppts = filteredAppointments?.filter(ap => {
              const apDate = new Date(ap.appointmentDateTime || ap.apoointmentDetails?.appointmentDateTime);
              return isValid(apDate) && apDate >= dayStart && apDate <= dayEnd;
          });

          // Use end of day as reference for past days to ensure "missing" is counted correctly
          const refDate = isSameDay(d, new Date()) ? new Date() : dayEnd;
          const kpis = calculateLiveKPIs(dayAppts, refDate); 
          
          days.push({
              date: format(d, "MMM d"),
              provided: kpis.completed,
              late: kpis.lateSubmissions,
              missing: kpis.missingProofs
          });
      }
      return days;
  }, [filteredAppointments, period]);

  // 12. Risk Distribution (Pie Chart)
  const riskDistribution = useMemo(() => {
      let high = 0, medium = 0, low = 0, none = 0;
      
      clients.forEach(site => {
          // Assuming properties exist, defaulting to false if not
          const proofRequired = site.proofRequired === true; 
          const autoReminders = site.autoReminders === true;
          const geofencing = site.geofencing === true;

          if (!proofRequired) {
              none++;
          } else {
              if (autoReminders) {
                  if (geofencing) {
                      high++;
                  } else {
                      medium++;
                  }
              } else {
                  low++;
              }
          }
      });

      // Fallback if no clients loaded yet to avoid empty chart
      if (clients.length === 0) return [];

      return [
          { name: "High", value: high, color: "#10b981" },
          { name: "Medium", value: medium, color: "#3b82f6" },
          { name: "Low", value: low, color: "#f59e0b" },
          { name: "None", value: none, color: "#ef4444" }
      ].filter(i => i.value > 0);
  }, [clients]);

  // 13. Today vs Prior Period (Bar Chart)
  const todayVsPriorData = useMemo(() => {
      const todayStart = startOfDay(now);
      const priorStart = subDays(todayStart, 7);

      const todayAppts = filteredAppointments?.filter(ap => isSameDay(new Date(ap.appointmentDateTime || ap.apoointmentDetails?.appointmentDateTime), todayStart));
      const priorAppts = filteredAppointments?.filter(ap => isSameDay(new Date(ap.appointmentDateTime || ap.apoointmentDetails?.appointmentDateTime), priorStart));

      const todayKPI = calculateLiveKPIs(todayAppts, now);
      // For prior, we simulate "end of day" reference to see final status
      const priorRefDate = new Date(priorStart);
      priorRefDate.setHours(23, 59, 59, 999);
      const priorKPI = calculateLiveKPIs(priorAppts, priorRefDate);

      return [
          { metric: "Compliance", today: todayKPI.shiftCompliance, prior: priorKPI.shiftCompliance },
          { metric: "Provided", today: todayKPI.completed, prior: priorKPI.completed },
          { metric: "Late", today: todayKPI.lateSubmissions, prior: priorKPI.lateSubmissions },
          { metric: "Missing", today: todayKPI.missingProofs, prior: priorKPI.missingProofs }
      ];
  }, [filteredAppointments]);

  const handleEmployeeRowClick = (emp) => {
      if (setSelectedEmployee) setSelectedEmployee(emp);
      if (setEmployeeHistoryTimeFrame) setEmployeeHistoryTimeFrame('last7');
      if (setShowEmployeeDetailModal) setShowEmployeeDetailModal(true);
  };

  const handleSendReminder = (ids) => {
      if (setMessageRecipients) setMessageRecipients(ids);
      if (setShowMessageModal) setShowMessageModal(true);
  };

  return (
    <>
      <div className="space-y-1 mb-6">
        <h2 className="text-lg font-semibold tracking-tight dark:text-white">Team Summary</h2>
        <p className="text-sm text-muted-foreground">
          {period === "today" ? "Today's" : "Selected period"} key metrics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KPICard
          label="Shift Compliance"
          value={`${kpi?.shiftCompliance}%`}
          delta={kpi?.shiftCompliance - yesterdayKpi?.shiftCompliance}
          icon={<TrendingUp className="h-5 w-5" />}
          status="success"
        />
        <KPICard
          label="Shifts"
          value={kpi?.missingProofs}
          sublabel="Missing Proofs"
          delta={yesterdayKpi?.missingProofs - kpi?.missingProofs}
          icon={<AlertTriangle className="h-5 w-5" />}
          status="warning"
        />
        <KPICard
          label="Late Submissions"
          value={kpi?.lateSubmissions}
          delta={yesterdayKpi?.lateSubmissions - kpi?.lateSubmissions}
          icon={<Clock className="h-5 w-5" />}
          status="warning"
        />
      </div>

      <div className="space-y-1 mb-6">
        <h2 className="text-lg font-semibold tracking-tight dark:text-white">Today's Activity</h2>
        <p className="text-sm text-muted-foreground">
          Current shift status and progress
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatusCard
          label="In Progress"
          value={kpi?.inProgress}
          variant="info"
        />
        <StatusCard
          label="Proof Due Soon"
          value={kpi?.proofDueSoon}
          variant="warning"
        />
        <StatusCard
          label="Overdue Proof"
          value={kpi?.overdueProof}
          variant="danger"
        />
        <StatusCard
          label="Completed"
          value={kpi?.completed}
          variant="success"
        />
      </div>

      <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
        <CardHeader className="p-16">
          <CardTitle className="text-lg font-bold dark:text-white">
            Shift Compliance Trend
          </CardTitle>
          <CardDescription>
            Daily compliance percentage over the selected period
          </CardDescription>
        </CardHeader>
        <CardContent className="p-16">
          {trend?.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No data available for the selected period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
              <LineChart
                data={trend}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: isMobile ? 11 : 12 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: isMobile ? 11 : 12 }}
                  width={isMobile ? 35 : 45}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip formatter={(value) => [`${value}%`, "Compliance"]} />
                <Legend
                  wrapperStyle={{ fontSize: isMobile ? "12px" : "14px" }}
                />
                <Line
                  type="monotone"
                  dataKey="compliance"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Current Period"
                />
                <Line
                  type="monotone"
                  dataKey="priorPeriod"
                  stroke="#6b7280"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Prior Period"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {showAdvancedView && (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
            <CardHeader className="p-16">
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <Users className="h-5 w-5 text-orange-600" />
                At-Risk Employees
              </CardTitle>
              <CardDescription>
                Employees with repeated missed or late proofs
              </CardDescription>
            </CardHeader>
            <CardContent className="p-16">
            {atRiskEmployees?.length > 0 ? (
              <>
              <ScrollArea className="h-auto max-h-[400px]">
                <TooltipProvider>
                  <div className="space-y-2">
                    {atRiskEmployees?.map((emp, idx) => (
                      <UITooltip key={emp.id} delayDuration={300}>
                        <TooltipTrigger asChild>
                          <div
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-all cursor-pointer group"
                            onClick={() => handleEmployeeRowClick(emp)}
                            style={{ animationDelay: `${idx * 50}ms` }}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Grey person icon instead of colored avatar */}
                              <div className="h-10 w-10 rounded-full bg-muted border-2 border-background flex items-center justify-center group-hover:scale-110 transition-transform shrink-0">
                                <Users className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">
                                  {emp.name}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  Most Recent: {emp.site}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 shrink-0">
                              <div className="text-right hidden sm:block">
                                <p className="text-xs text-muted-foreground">
                                  Missing: {emp.missingProofs}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Late: {emp.lateProofs}
                                </p>
                              </div>
                              <Badge
                                variant={
                                  emp.completionRate < 75
                                    ? "destructive"
                                    : "secondary"
                                }
                              >
                                {emp.completionRate}%
                              </Badge>
                              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs p-4">
                          <div className="space-y-2">
                            <p className="font-bold text-sm">{emp.name}</p>
                            <p className="text-xs text-muted-foreground">
                              Most Recent: {emp.site}
                            </p>
                            <Separator className="my-2" />
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div>
                                <span className="text-muted-foreground">
                                  Missing:
                                </span>
                                <span className="font-semibold ml-1 text-red-600">
                                  {emp.missingProofs}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Late:</span>
                                <span className="font-semibold ml-1 text-orange-600">
                                  {emp.lateProofs}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Complete:
                                </span>
                                <span className="font-semibold ml-1 text-green-600">
                                  {emp.completed}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Rate:</span>
                                <span className="font-semibold ml-1">
                                  {emp.completionRate}%
                                </span>
                              </div>
                            </div>
                            <Separator className="my-2" />
                            <p className="text-xs text-muted-foreground">
                              Last shift: {emp.lastShift}
                            </p>
                            <p className="text-xs text-primary font-medium mt-2">
                              Click for full details
                            </p>
                          </div>
                        </TooltipContent>
                      </UITooltip>
                    ))}
                  </div>
                </TooltipProvider>
              </ScrollArea>

              <div className="mt-4 pt-4 border-t">
                <Button
                  onClick={() =>
                    handleSendReminder(atRiskEmployees.map((e) => e.id))
                  }
                  className="w-full"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Reminder to All
                </Button>
              </div>
              </>
              ) : (
                <div className="flex flex-col items-center justify-center text-center h-full">
                  <p className="text-sm font-medium">
                    No at-risk employees found
                  </p>
                  <p className="text-xs mt-4 text-muted-foreground">
                    Employees will appear here once shifts with incomplete or late submissions are detected
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SECTION: Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Compliance Trend */}
            <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
              <CardHeader className="p-16">
                <CardTitle className="text-lg font-bold">
                  Compliance Trend Over Time
                </CardTitle>
                <CardDescription>
                  Current period vs prior period comparison
                </CardDescription>
              </CardHeader>
              <CardContent className="p-16">
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                  <LineChart
                    data={trend}
                    margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: isMobile ? 11 : 12 }} />
                    <YAxis
                      tick={{ fontSize: isMobile ? 11 : 12 }}
                      domain={[0, 100]}
                      width={isMobile ? 35 : 45}
                    />
                    <Tooltip />
                    <Legend
                      wrapperStyle={{ fontSize: isMobile ? "12px" : "14px" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="compliance"
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Current Period"
                    />
                    <Line
                      type="monotone"
                      dataKey="priorPeriod"
                      stroke="#6b7280"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name="Prior Period"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Proof Breakdown */}
            <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
              <CardHeader className="p-16">
                <CardTitle className="text-lg font-bold">Proof Breakdown</CardTitle>
                <CardDescription>
                  Provided, late, and missing proof distribution
                </CardDescription>
              </CardHeader>
              <CardContent className="p-16">
                <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                  <AreaChart
                    data={proofBreakdownData}
                    margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" tick={{ fontSize: isMobile ? 11 : 12 }} />
                    <YAxis
                      tick={{ fontSize: isMobile ? 11 : 12 }}
                      width={isMobile ? 35 : 45}
                    />
                    <Tooltip />
                    <Legend
                      wrapperStyle={{ fontSize: isMobile ? "12px" : "14px" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="provided"
                      stackId="1"
                      stroke="#10b981"
                      fill="#10b981"
                      opacity={0.8}
                      name="Provided"
                    />
                    <Area
                      type="monotone"
                      dataKey="late"
                      stackId="1"
                      stroke="#f59e0b"
                      fill="#f59e0b"
                      opacity={0.8}
                      name="Late"
                    />
                    <Area
                      type="monotone"
                      dataKey="missing"
                      stackId="1"
                      stroke="#ef4444"
                      fill="#ef4444"
                      opacity={0.8}
                      name="Missing"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card className="shadow-md dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
            <CardHeader className="p-16">
              <CardTitle className="text-lg font-bold">Risk Distribution</CardTitle>
              <CardDescription>
                Proof policy coverage impact on compliance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 p-16">
              {/* Pie Chart - NO internal labels */}
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
                <RechartsPieChart>
                  <Pie
                    data={riskDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={isMobile ? 70 : 90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {riskDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>

              {/* External Legend Table - Mobile Readable */}
              <div className="space-y-2 px-2">
                {riskDistribution.map((entry) => {
                  const total = riskDistribution.reduce(
                    (sum, item) => sum + item.value,
                    0,
                  );
                  const percentage = Math.round((entry.value / total) * 100);
                  return (
                    <div
                      key={entry.name}
                      className="flex items-center justify-between py-2 border-b last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="h-3 w-3 rounded-full shrink-0"
                          style={{ backgroundColor: entry.color }}
                        />
                        <span className="text-sm font-medium">
                          {entry.name} Enforcement
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold">
                          {entry.value} sites
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {percentage}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-sm text-muted-foreground text-center pt-2 leading-relaxed border-t">
                Insight: Sites with Medium to High proof enforcement show 25% better
                compliance rates.
              </p>
            </CardContent>
          </Card>

          {/* Today vs Prior Period */}
          <Card className="shadow-md border-2 border-primary/20 dark:bg-[#1A1F2C] dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
            <CardHeader className="p-16">
              <CardTitle className="text-xl font-bold flex items-center gap-2">
                <Activity className="h-5 w-5 text-primary" />
                Today vs Prior Period
              </CardTitle>
              <CardDescription>
                Key metrics comparison - conclusion view
              </CardDescription>
            </CardHeader>
            <CardContent className="p-16">
              <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                <BarChart
                  data={todayVsPriorData}
                  layout="vertical"
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: isMobile ? 11 : 12 }}
                    domain={[0, "dataMax + 10"]}
                  />
                  <YAxis
                    dataKey="metric"
                    type="category"
                    tick={{ fontSize: isMobile ? 11 : 12 }}
                    width={isMobile ? 80 : 100}
                  />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: isMobile ? "12px" : "14px" }} />
                  <Bar
                    dataKey="today"
                    fill="#10b981"
                    name="Today"
                    radius={[0, 4, 4, 0]}
                  />
                  <Bar
                    dataKey="prior"
                    fill="#6b7280"
                    name="Prior Period"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        )}
    </>
  );
};

export default OverviewHeader;
